import { useQuery } from "@tanstack/react-query";
import { generateSlug, getSolutionBySlug } from "../utils/slugUtils";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://insightpanel.sembian.net";

/**
 * Parse HTML string and extract list items as array
 * @param {string} htmlString - HTML string containing <ul><li> elements
 * @returns {Array<string>} Array of HTML content from each <li> element
 */
const parseHtmlListItems = (htmlString) => {
  if (!htmlString) return [];

  try {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Find all <li> elements
    const listItems = tempDiv.querySelectorAll("li");

    // Extract inner HTML of each <li> as array
    return Array.from(listItems).map((li) => li.innerHTML.trim());
  } catch (e) {
    console.warn("Error parsing HTML list items:", e);
    // Fallback: if parsing fails, return the original HTML as single item
    return [htmlString];
  }
};

/**
 * Transform API solution data to match component expectations
 */
const transformSolution = (apiSolution) => {
  // Parse feature JSON string to array
  let features = [];
  try {
    features = JSON.parse(apiSolution.feature || "[]");
  } catch (e) {
    console.warn("Error parsing features:", e);
    features = [];
  }

  // Parse advantage HTML string and extract list items
  const advantages = parseHtmlListItems(apiSolution.advantage);

  // Parse application HTML string and extract list items
  const applications = parseHtmlListItems(apiSolution.application);

  // Construct full image URL
  // url/assets/images/solution/1763787289.jpg
  const imageUrl = apiSolution.image
    ? `${API_BASE_URL}/assets/images/solution/${apiSolution.image}`
    : null;

  return {
    id: apiSolution.id,
    category: apiSolution.category?.name || "Other",
    name: apiSolution.name,
    image: imageUrl,
    overview: apiSolution.overview || "",
    features: features,
    advantages: advantages,
    applications: applications,
    other_details_html: apiSolution.other_detail || "",
    // Keep original data for reference if needed
    _original: apiSolution,
  };
};

/**
 * Get all solutions
 * @returns {Promise<Array>} Array of all solutions
 */
export const getAllSolutions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/solutions`);
    const result = await response.json();

    if (result.success && result.data) {
      // Transform each solution
      return result.data.map(transformSolution);
    }

    console.error("API response error:", result);
    return [];
  } catch (error) {
    console.error("Error fetching solutions:", error);
    return [];
  }
};

/**
 * Get solution by slug
 * @param {string} slug - Solution slug
 * @returns {Promise<Object|null>} Solution object or null if not found
 */
export const getSolution = async (slug) => {
  try {
    // First, get all solutions
    const solutions = await getAllSolutions();

    // Find solution by slug
    const solution = getSolutionBySlug(solutions, slug);
    return solution || null;
  } catch (error) {
    console.error("Error loading solution:", error);
    return null;
  }
};

/**
 * Get solutions by category
 * @param {string} category - Category name
 * @returns {Promise<Array>} Array of solutions in the category
 */
export const getSolutionsByCategory = async (category) => {
  try {
    const solutions = await getAllSolutions();
    return solutions.filter((s) => s.category === category);
  } catch (error) {
    console.error("Error fetching solutions by category:", error);
    return [];
  }
};

/**
 * Get all unique categories
 * @returns {Promise<Array>} Array of unique category names
 */
export const getCategories = async () => {
  try {
    const solutions = await getAllSolutions();
    const categories = [...new Set(solutions.map((s) => s.category))];
    return categories;
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

// Export utility functions for direct use if needed
export { generateSlug, getSolutionBySlug };

/**
 * React Query hook to get all solutions
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useSolutions = () => {
  return useQuery({
    queryKey: ["solutions"],
    queryFn: getAllSolutions,
    staleTime: 30 * 60 * 1000, // 30 minutes - solutions don't change frequently
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for 1 hour
  });
};

/**
 * React Query hook to get a single solution by slug
 * @param {string} slug - Solution slug
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useSolution = (slug) => {
  return useQuery({
    queryKey: ["solution", slug],
    queryFn: () => getSolution(slug),
    enabled: !!slug,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * React Query hook to get solutions by category
 * @param {string} category - Category name
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useSolutionsByCategory = (category) => {
  return useQuery({
    queryKey: ["solutions", "category", category],
    queryFn: () => getSolutionsByCategory(category),
    enabled: !!category,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * React Query hook to get all unique categories
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};
